import React, { useRef, useState } from 'react';
import { Box } from '@mui/material';

import AsyncActionButton, { AsyncActionButtonProps } from './AsyncActionButton';

type ExclusiveAsyncAction = Omit<AsyncActionButtonProps, 'disabled' | 'onAction'> & {
  actionId: string;
  disabled?: boolean;
  onAction: () => Promise<void>;
};

type ExclusiveAsyncActionGroupProps = {
  actions: ExclusiveAsyncAction[];
};

const ExclusiveAsyncActionGroup: React.FC<ExclusiveAsyncActionGroupProps> = ({ actions }) => {
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  const activeActionIdRef = useRef<string | null>(null);

  const lockActionGroup = (actionId: string) => {
    activeActionIdRef.current = actionId;
    setActiveActionId(actionId);
  };

  const unlockActionGroup = (actionId: string) => {
    if (activeActionIdRef.current !== actionId) {
      return;
    }

    activeActionIdRef.current = null;
    setActiveActionId(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        // REQ-005: stack on narrow mobile, then switch to a horizontal wrapping action row.
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: 1.5,
        width: '100%',
      }}
    >
      {actions.map(({ actionId, disabled = false, onAction, onAfterCycleComplete, ...buttonProps }) => (
        <AsyncActionButton
          {...buttonProps}
          key={actionId}
          disabled={disabled || activeActionId !== null}
          onAction={async () => {
            if (activeActionIdRef.current !== null) {
              return;
            }

            lockActionGroup(actionId);
            await onAction();
          }}
          onAfterCycleComplete={() => {
            unlockActionGroup(actionId);
            onAfterCycleComplete?.();
          }}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flex: { sm: '1 1 220px' },
            maxWidth: { sm: '320px' },
            ...(buttonProps.sx ?? {}),
          }}
        />
      ))}
    </Box>
  );
};

export default ExclusiveAsyncActionGroup;
